import { onSuccessRequest as setHasMore } from 'modules/global/reducers/generic.hasMore';
import { setAll as setAllIds, beforeRequest as beforeRequestIds } from 'modules/global/reducers/generic.ids';
import { isFailureRequest, isStartRequest, isSuccessRequest } from 'modules/global/reducers/utils';
import { createReducer } from 'utils/ReduxUtils';

/**
 * @typedef {{
 *  apiError: string,
 *  hasMore: boolean,
 *  ids: Array<string>,
 *  loading: boolean,
 *  page: import('modules/global/generic.types').Page,
 * }} SliceSelect
 *
 * @typedef {{
 *     [token:string] : SliceSelect,
 * }} State
 */

/** @type {State} */
const initialState = {};

/** @type {SliceSelect} */
const emptySlice = {
  hasMore: false,
  loading: false,
  apiError: null,
  ids: [],
  page: { number: 0, totalElements: 0, totalPages: 0, size: 10 },
};

/**
 * @param {SliceSelect} state
 * @param {import("modules/global/generic.types").Action<import("modules/global/generic.types").GetRequest<unknown>>} action
 * @returns {SliceSelect}
 */
const onRequest = (state, action, config) => ({
  ...state,
  loading: true,
  apiError: null,
  ids: beforeRequestIds(config)(state.ids, action),
});

/**
 * @param {SliceSelect} state
 * @param {import("modules/global/generic.types").Action<import("modules/global/generic.types").PageContent<unknown>>} action
 * @returns {SliceSelect}
 */
const onSuccessRequest = (state, action, config) => {
  const { payload } = action;

  return {
    ...state,
    loading: false,
    page: payload.page,
    ids: setAllIds(config)(state.ids, action),
    hasMore: setHasMore(state.hasMore, action),
  };
};

/**
 * @param {SliceSelect} state
 * @param {import("modules/global/generic.types").ErrorAction} action
 * @returns {SliceSelect}
 */
const onFailureRequest = (_, { payload: error }) => ({
  ...emptySlice,
  apiError: error,
});

/**
 * @param {import("modules/global/generic.types").Action<unknown>} action
 * @returns {string}
 */
const getToken = action => action.meta?.selectToken;

/**
 * @param {import("modules/global/generic.types").Action<unknown>} action
 * @returns {boolean}
 */
const selectMatcher = action => getToken(action) !== undefined;

const selectReducer = (actionRoot, config) =>
  createReducer(initialState, {}, [
    {
      matcher: isStartRequest(actionRoot, selectMatcher),
      reducer: (state, action) => {
        const token = getToken(action);
        return {
          ...state,
          [token]: onRequest(state[token] ?? emptySlice, action, config?.global),
        };
      },
    },
    {
      matcher: isSuccessRequest(actionRoot, selectMatcher),
      reducer: (state, action) => {
        const token = getToken(action);
        return {
          ...state,
          [token]: onSuccessRequest(state[token] ?? emptySlice, action, config?.global),
        };
      },
    },
    {
      matcher: isFailureRequest(actionRoot, selectMatcher),
      reducer: (state, action) => {
        const token = getToken(action);
        return {
          ...state,
          [token]: onFailureRequest(state[token] ?? emptySlice, action),
        };
      },
    },
  ]);

export default selectReducer;
